import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    batchesAPI, suppliersAPI, productsAPI, settingsAPI,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
    Boxes, Plus, Search, Loader2, AlertTriangle, ShieldOff, Trash2, Download, FileWarning,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Bucket → color metadata. Labels are translated at render time via the t() callback.
export const BUCKET_KEYS = ['expired', '30', '60', '90', '180', 'safe'];
export const BUCKET_COLORS = {
    expired: 'bg-[#7F1D1D] text-white',
    '30': 'bg-[#E76F51] text-white',
    '60': 'bg-[#F4A261] text-white',
    '90': 'bg-[#E9C46A] text-[#1A1A1A]',
    '180': 'bg-[#2A9D8F]/30 text-[#2A9D8F]',
    safe: 'bg-[#2A9D8F]/15 text-[#2A9D8F]',
};
// Backwards-compat for any callers that still expect the legacy untranslated array
export const BUCKETS = BUCKET_KEYS.map((key) => ({ key, color: BUCKET_COLORS[key] }));

export const ExpiryBadge = ({ bucket, days }) => {
    const { t } = useTranslation('entities');
    const color = BUCKET_COLORS[bucket] || BUCKET_COLORS.safe;
    let detail = '';
    if (days != null) {
        if (days < 0) detail = ` · ${Math.abs(days)}d ago`;
        else detail = ` · ${days}d`;
    }
    return (
        <Badge className={`${color} text-[10px] font-semibold`} data-testid={`bucket-${bucket}`}>
            {t(`batches.buckets.${bucket}`)}{detail}
        </Badge>
    );
};

export const StatusBadge = ({ status }) => {
    const { t } = useTranslation('entities');
    const map = {
        active: 'bg-[#2A9D8F]/20 text-[#2A9D8F]',
        depleted: 'bg-gray-200 text-gray-600',
        recalled: 'bg-[#E76F51] text-white',
        expired: 'bg-[#7F1D1D] text-white',
    };
    const label = t(`batches.statuses.${status}`, { defaultValue: status });
    return <Badge className={`${map[status] || 'bg-gray-300 text-gray-700'} text-[10px] uppercase tracking-wider`}>{label}</Badge>;
};

const emptyBatch = {
    product_id: '',
    batch_number: '',
    manufacturing_date: '',
    expiry_date: '',
    quantity_received: '',
    supplier_id: '',
    warehouse: '',
    cost_price: '',
    notes: '',
};

const Batches = () => {
    const { t } = useTranslation(['entities', 'common']);
    const { isAdmin } = useAuth();
    const [items, setItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [defaultWh, setDefaultWh] = useState('Main Warehouse');
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ search: '', product_id: '', supplier_id: '', bucket: '', status: '' });
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyBatch);
    const [saving, setSaving] = useState(false);

    // Recall dialog
    const [recallTarget, setRecallTarget] = useState(null);
    const [recallReason, setRecallReason] = useState('');
    const [recalling, setRecalling] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.search) params.search = filters.search;
            if (filters.product_id) params.product_id = filters.product_id;
            if (filters.supplier_id) params.supplier_id = filters.supplier_id;
            if (filters.bucket) params.bucket = filters.bucket;
            if (filters.status) params.status = filters.status;
            const { data } = await batchesAPI.getAll(params);
            setItems(data);
        } catch {
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    const fetchMasters = async () => {
        try {
            const [p, s, wh] = await Promise.all([
                productsAPI.getAll(),
                suppliersAPI.getAll(),
                settingsAPI.getWarehouse(),
            ]);
            setProducts(p.data);
            setSuppliers(s.data);
            setDefaultWh(wh.data?.warehouse || 'Main Warehouse');
        } catch {
            /* silent */
        }
    };

    useEffect(() => { fetchMasters(); }, []);
    useEffect(() => {
        const t = setTimeout(fetchData, 250);
        return () => clearTimeout(t);
    }, [filters]);

    const filtersActive = useMemo(
        () => !!(filters.search || filters.product_id || filters.supplier_id || filters.bucket || filters.status),
        [filters],
    );

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyBatch, warehouse: defaultWh });
        setOpen(true);
    };
    const openEdit = (b) => {
        setEditing(b);
        setForm({
            product_id: b.product_id,
            batch_number: b.batch_number,
            manufacturing_date: b.manufacturing_date || '',
            expiry_date: b.expiry_date || '',
            quantity_received: String(b.quantity_received ?? ''),
            quantity_remaining: String(b.quantity_remaining ?? ''),
            supplier_id: b.supplier_id || '',
            warehouse: b.warehouse || '',
            cost_price: String(b.cost_price ?? ''),
            notes: b.notes || '',
        });
        setOpen(true);
    };

    const handleSave = async () => {
        if (!editing && !form.product_id) { toast.error('Product is required'); return; }
        if (!form.batch_number?.trim()) { toast.error('Batch number is required'); return; }
        if (!form.expiry_date) { toast.error('Expiry date is required'); return; }
        const payload = {
            ...form,
            quantity_received: parseFloat(form.quantity_received || 0),
            cost_price: parseFloat(form.cost_price || 0),
            supplier_id: form.supplier_id || null,
            manufacturing_date: form.manufacturing_date || null,
        };
        if (editing) {
            // Only allow safe fields on update — strip product_id
            delete payload.product_id;
            if (form.quantity_remaining !== undefined && form.quantity_remaining !== '') {
                payload.quantity_remaining = parseFloat(form.quantity_remaining);
            }
        }
        setSaving(true);
        try {
            if (editing) {
                await batchesAPI.update(editing.id, payload);
                toast.success(t('entities:batches.toasts.updated'));
            } else {
                await batchesAPI.create(payload);
                toast.success(t('entities:batches.toasts.created'));
            }
            setOpen(false);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common:toasts.save_failed'));
        } finally { setSaving(false); }
    };

    const handleDelete = async (b) => {
        if (!window.confirm(`${t('common:actions.delete')} ${b.batch_number}?`)) return;
        try {
            await batchesAPI.delete(b.id);
            toast.success(t('entities:batches.toasts.deleted'));
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common:toasts.delete_failed'));
        }
    };

    const confirmRecall = async () => {
        if (!recallReason.trim()) { toast.error(t('entities:batches.recall.reason_required')); return; }
        setRecalling(true);
        try {
            await batchesAPI.recall(recallTarget.id, recallReason.trim());
            toast.success(t('entities:batches.toasts.recalled', { number: recallTarget.batch_number }));
            setRecallTarget(null); setRecallReason('');
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common:toasts.save_failed'));
        } finally { setRecalling(false); }
    };

    const exportAffectedCustomers = async (b) => {
        try {
            const { data } = await batchesAPI.affectedCustomers(b.id);
            if (!data.affected_count) {
                toast.info(t('entities:batches.toasts.no_customers'));
                return;
            }
            const headers = ['Invoice', 'Date', 'Company', 'Contact', 'Email', 'Mobile', 'Qty Sold', 'Line Total (EUR)'];
            const esc = (v) => {
                if (v == null) return '';
                const s = String(v);
                return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const rows = data.entries.map((e) => [
                e.invoice_number, e.created_at, e.company_name, e.contact_person,
                e.email_address, e.mobile_number, e.quantity_sold, e.line_total,
            ]);
            const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `recall_${data.batch_number}_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(t('entities:batches.toasts.exported', { count: data.affected_count }));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common:toasts.load_failed'));
        }
    };

    return (
        <div data-testid="batches-page" className="space-y-8 animate-fadeIn">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">{t('entities:batches.section_label')}</p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:batches.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('entities:batches.subtitle')}</p>
                </div>
                <Button onClick={openCreate} data-testid="new-batch-btn" className="bg-[#1A1A1A] hover:bg-[#C08478] text-white">
                    <Plus size={16} className="mr-2" /> {t('entities:batches.add')}
                </Button>
            </div>

            {/* Filter bar */}
            <Card className="border-0 shadow-sm">
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
                    <div className="relative md:col-span-2">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                            data-testid="batch-search"
                            placeholder={t('entities:batches.search_placeholder')}
                            value={filters.search}
                            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                            className="pl-9 bg-[#F5F5F5] border-[#E5E5E5]"
                        />
                    </div>
                    <Select value={filters.product_id || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, product_id: v === 'all' ? '' : v }))}>
                        <SelectTrigger data-testid="filter-product"><SelectValue placeholder={t('entities:batches.filters.product')} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('entities:batches.filters.all_products')}</SelectItem>
                            {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filters.supplier_id || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, supplier_id: v === 'all' ? '' : v }))}>
                        <SelectTrigger data-testid="filter-supplier"><SelectValue placeholder={t('entities:batches.filters.supplier')} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('entities:batches.filters.all_suppliers')}</SelectItem>
                            {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filters.bucket || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, bucket: v === 'all' ? '' : v }))}>
                        <SelectTrigger data-testid="filter-bucket"><SelectValue placeholder={t('entities:batches.filters.expiry')} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('entities:batches.filters.all_expiry')}</SelectItem>
                            {BUCKETS.map((b) => <SelectItem key={b.key} value={b.key}>{t(`entities:batches.buckets.${b.key}`)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filters.status || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? '' : v }))}>
                        <SelectTrigger data-testid="filter-status"><SelectValue placeholder={t('entities:batches.filters.status')} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('entities:batches.filters.all_statuses')}</SelectItem>
                            <SelectItem value="active">{t('entities:batches.statuses.active')}</SelectItem>
                            <SelectItem value="depleted">{t('entities:batches.statuses.depleted')}</SelectItem>
                            <SelectItem value="recalled">{t('entities:batches.statuses.recalled')}</SelectItem>
                            <SelectItem value="expired">{t('entities:batches.statuses.expired')}</SelectItem>
                        </SelectContent>
                    </Select>
                    {filtersActive && (
                        <Button variant="ghost" data-testid="clear-filters"
                                onClick={() => setFilters({ search: '', product_id: '', supplier_id: '', bucket: '', status: '' })}
                                className="md:col-span-6 justify-self-start text-xs text-[#C08478]">
                            {t('common:actions.clear_filters')}
                        </Button>
                    )}
                </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Boxes className="text-[#C08478]" size={20} />
                        {t('entities:batches.title')} ({items.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#C08478]" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-gray-400">
                            {t('entities:batches.empty')}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('entities:batches.columns.batch')}</TableHead>
                                        <TableHead>{t('entities:batches.columns.product')}</TableHead>
                                        <TableHead>{t('entities:batches.columns.supplier')}</TableHead>
                                        <TableHead>{t('entities:batches.columns.warehouse')}</TableHead>
                                        <TableHead>{t('entities:batches.columns.mfg')}</TableHead>
                                        <TableHead>{t('entities:batches.columns.expiry')}</TableHead>
                                        <TableHead className="text-right">{t('entities:batches.columns.received')}</TableHead>
                                        <TableHead className="text-right">{t('entities:batches.columns.remaining')}</TableHead>
                                        {isAdmin() && <TableHead className="text-right">{t('entities:batches.columns.cost')}</TableHead>}
                                        <TableHead>{t('common:common.status')}</TableHead>
                                        <TableHead className="text-right">{t('common:common.actions')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {items.map((b) => (
                                        <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                                            <TableCell className="font-mono font-semibold text-sm">{b.batch_number}</TableCell>
                                            <TableCell>
                                                <p className="text-sm font-medium">{b.product_name}</p>
                                                <p className="text-[10px] text-gray-400 font-mono">{b.sku}</p>
                                            </TableCell>
                                            <TableCell className="text-sm">{b.supplier_name || '-'}</TableCell>
                                            <TableCell className="text-sm">{b.warehouse || '-'}</TableCell>
                                            <TableCell className="font-mono text-xs">{b.manufacturing_date || '-'}</TableCell>
                                            <TableCell className="font-mono text-xs">
                                                <div className="flex items-center gap-2">
                                                    <span>{b.expiry_date}</span>
                                                    <ExpiryBadge bucket={b.expiry_bucket} days={b.days_to_expiry} />
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right font-mono">{b.quantity_received}</TableCell>
                                            <TableCell className="text-right font-mono font-semibold">{b.quantity_remaining}</TableCell>
                                            {isAdmin() && <TableCell className="text-right font-mono text-sm">{b.cost_price?.toFixed?.(2) ?? b.cost_price}</TableCell>}
                                            <TableCell><StatusBadge status={b.status} /></TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="icon" onClick={() => exportAffectedCustomers(b)}
                                                            title="Export customers who received this batch"
                                                            data-testid={`export-affected-${b.id}`}
                                                            className="text-gray-500 hover:text-[#C08478]">
                                                        <Download size={15} />
                                                    </Button>
                                                    {b.status === 'active' && isAdmin() && (
                                                        <Button variant="ghost" size="icon"
                                                                onClick={() => { setRecallTarget(b); setRecallReason(''); }}
                                                                data-testid={`recall-${b.id}`}
                                                                title="Recall this batch"
                                                                className="text-gray-500 hover:text-[#E76F51]">
                                                            <ShieldOff size={15} />
                                                        </Button>
                                                    )}
                                                    {b.quantity_remaining === b.quantity_received && isAdmin() && (
                                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(b)} title="Delete (only if untouched)"
                                                                className="text-gray-500 hover:text-red-500">
                                                            <Trash2 size={15} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="batch-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? t('entities:batches.dialog.edit', { number: editing.batch_number }) : t('entities:batches.dialog.new')}</DialogTitle>
                        <DialogDescription>
                            {editing ? t('entities:batches.dialog.description_edit') : t('entities:batches.dialog.description_new')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                            <Label>Product *</Label>
                            <Select value={form.product_id || ''} disabled={!!editing}
                                    onValueChange={(v) => setForm({ ...form, product_id: v })}>
                                <SelectTrigger data-testid="batch-product"><SelectValue placeholder="Pick a product" /></SelectTrigger>
                                <SelectContent>
                                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Batch / Lot Number *</Label>
                            <Input data-testid="batch-number" value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} className="font-mono" />
                        </div>
                        <div className="space-y-2">
                            <Label>Supplier</Label>
                            <Select value={form.supplier_id || 'none'} onValueChange={(v) => setForm({ ...form, supplier_id: v === 'none' ? '' : v })}>
                                <SelectTrigger data-testid="batch-supplier"><SelectValue placeholder="(none)" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">(none)</SelectItem>
                                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Manufacturing Date</Label>
                            <Input type="date" data-testid="batch-mfg" value={form.manufacturing_date}
                                   onChange={(e) => setForm({ ...form, manufacturing_date: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Expiry Date *</Label>
                            <Input type="date" data-testid="batch-expiry" value={form.expiry_date}
                                   onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Quantity Received *</Label>
                            <Input data-testid="batch-qty" type="number" step="0.01" min="0" value={form.quantity_received}
                                   onChange={(e) => setForm({ ...form, quantity_received: e.target.value })} />
                        </div>
                        {editing && (
                            <div className="space-y-2">
                                <Label>Quantity Remaining</Label>
                                <Input data-testid="batch-remaining" type="number" step="0.01" min="0" value={form.quantity_remaining ?? ''}
                                       onChange={(e) => setForm({ ...form, quantity_remaining: e.target.value })} />
                                <p className="text-[10px] text-gray-400">Manually editing rebalances product stock.</p>
                            </div>
                        )}
                        {isAdmin() && (
                            <div className="space-y-2">
                                <Label>Cost Price (€)</Label>
                                <Input data-testid="batch-cost" type="number" step="0.01" min="0" value={form.cost_price}
                                       onChange={(e) => setForm({ ...form, cost_price: e.target.value })} />
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>Warehouse</Label>
                            <Input data-testid="batch-warehouse" value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })}
                                   placeholder={defaultWh} />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label>Notes</Label>
                            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-batch"
                                className="bg-[#1A1A1A] hover:bg-[#C08478] text-white">
                            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                            {editing ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Recall confirm */}
            <AlertDialog open={!!recallTarget} onOpenChange={(o) => { if (!o) { setRecallTarget(null); setRecallReason(''); } }}>
                <AlertDialogContent data-testid="recall-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle size={18} className="text-[#E76F51]" />
                            {recallTarget && t('entities:batches.recall.title', { number: recallTarget.batch_number })}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {recallTarget && t('entities:batches.recall.description', { qty: `${recallTarget.quantity_remaining} ${t('entities:expiry.units_suffix')}` })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2">
                        <Label>{t('entities:batches.recall.reason')} *</Label>
                        <Textarea data-testid="recall-reason" rows={3} value={recallReason}
                                  onChange={(e) => setRecallReason(e.target.value)}
                                  placeholder={t('entities:batches.recall.reason_placeholder')} />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction data-testid="recall-confirm" onClick={confirmRecall} disabled={recalling}
                                           className="bg-[#E76F51] hover:bg-[#d35a3d] text-white">
                            {recalling && <Loader2 size={14} className="mr-2 animate-spin" />}
                            <FileWarning size={14} className="mr-2" />
                            {t('entities:batches.recall.button')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default Batches;
